/**
 * Platform Job Service
 * Handles job execution and monitoring on OptiLogic platform
 */

import { getUserAppKey } from './optilogicService';

const API_BASE_URL = 'https://api.optilogic.app/v0/Studio/job';

/**
 * Run a job on the platform
 * @param {string} directoryPath - Platform directory path where the job file is located
 * @param {string} filename - Name of the Python file to execute
 * @returns {Promise<string>} Job key for monitoring
 */
export async function runJob(directoryPath, filename) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const url = `${API_BASE_URL}?directoryPath=${encodeURIComponent(directoryPath)}&filename=${encodeURIComponent(filename)}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'X-APP-KEY': appKey
        }
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Job execution failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();

    if (!result.jobKey) {
        throw new Error('Job execution did not return a jobKey');
    }

    return result.jobKey;
}

/**
 * Get job status
 * @param {string} jobKey - Job key returned from runJob
 * @returns {Promise<string>} Job status: "done", "error", "cancelled", "running", etc.
 */
export async function getJobStatus(jobKey) {
    const appKey = getUserAppKey();

    if (!appKey) {
        throw new Error('Not authenticated. Please log in first.');
    }

    const url = `${API_BASE_URL}/${encodeURIComponent(jobKey)}?op=status`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'X-APP-KEY': appKey
        }
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Job status check failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    return result.status;
}

/**
 * Monitor job until completion
 * @param {string} jobKey - Job key returned from runJob
 * @param {number} pollIntervalSeconds - How often to check status (default 5 seconds)
 * @param {Function} onStatusUpdate - Optional callback(status) called on each status check
 * @returns {Promise<string>} Final status: "done", "error", or "cancelled"
 */
export async function monitorJob(jobKey, pollIntervalSeconds = 5, onStatusUpdate = null) {
    let status = '';

    while (status !== 'done' && status !== 'error' && status !== 'cancelled') {
        // Wait before checking status
        await new Promise(resolve => setTimeout(resolve, pollIntervalSeconds * 1000));

        status = await getJobStatus(jobKey);

        if (onStatusUpdate) {
            onStatusUpdate(status);
        }
    }

    if (status === 'error') {
        throw new Error('Job failed to run');
    }

    if (status === 'cancelled') {
        throw new Error('Job was cancelled');
    }

    return status;
}
